import React, { MutableRefObject } from 'react';
import styled from 'styled-components';
import {
  StrapiTextual,
  StrapiTextualProps,
} from '@/features/strapi/components/textual';
import { breakpoints } from '@/components/responsive';
import { VSpace } from '@/components/spacing';

export interface StrapiCaseStudyCardProps {
  bigTitle: StrapiTextualProps;
  title: StrapiTextualProps;
  teaser: StrapiTextualProps;
  coverImage: string;
  logoImage: string;
  link: string;
  opacity?: number;

  onMouseEnter?: (ev) => void;
  onMouseLeave?: (ev) => void;

  ref?: MutableRefObject<any>;
}

export function StrapiCaseStudyCard({
  bigTitle,
  title,
  teaser,
  coverImage,
  link,
  opacity,
  onMouseEnter,
  onMouseLeave,
  ref,
}: StrapiCaseStudyCardProps) {
  return (
    <Wrapper ref={ref} opacity={opacity}>
      <Left onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <StrapiTextual className="big-title only-desktop" {...bigTitle} />
        <StrapiTextual className="title only-desktop" {...title} />
        <VSpace className="only-desktop" value={2} />
        <div
          className="only-mobile"
          style={{
            display: `grid`,
            gridTemplateColumns: `min-content max-content`,
            gridGap: `0.5rem`,
            marginBottom: `1.5rem`,
          }}
        >
          <StrapiTextual className="big-title" {...bigTitle} size="3.2rem" />
          <StrapiTextual className="title" {...title} size="3.2rem" />
        </div>
        <StrapiTextual className="teaser" {...teaser} size="1.8rem" />
        <ReadMore className="only-desktop" href={link}>
          Read More {`>`}
        </ReadMore>
      </Left>
      <Right>
        <img src={coverImage} alt="" />
        <ReadMore className="only-mobile" href={link}>
          Read More {`>`}
        </ReadMore>
      </Right>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  min-height: 70rem;
  display: grid;
  grid-template-columns: 40% 60%;

  align-content: flex-start;
  justify-content: flex-start;

  background-color: #ffffff;
  box-shadow: 0 18px 46px 0 rgba(185, 166, 174, 0.36);
  border-radius: 3rem;
  overflow: hidden;

  transition: opacity 0.5s ease-in-out;
  opacity: ${(props: { opacity?: number }) =>
    props.opacity ? props.opacity : 1};

  ${breakpoints.down(`md`)} {
    min-height: 0;
    grid-template-columns: 1fr;
    grid-template-rows: 32rem 25rem;
    border-radius: 2rem;
  }
`;

const Left = styled.div`
  display: grid;
  justify-items: flex-start;
  align-content: flex-start;
  height: 100%;
  width: 100%;
  padding: 4rem 6rem;

  ${breakpoints.down(`md`)} {
    padding: 3rem 2rem 3rem 3rem;
  }

  && > * {
    text-align: start;
  }

  .big-title {
    font-family: Inter, serif;

    ${breakpoints.down(`md`)} {
      font-family: Eesti, serif;
    }
  }

  .big-title,
  .title {
    color: #3b47ff;
    letter-spacing: -0.5px;
    margin-top: 1rem;
    ${breakpoints.down(`md`)} {
      margin: 0;
    }
  }

  .teaser {
    line-height: 1.8;
  }
`;

const Right = styled.div`
  align-content: stretch;
  height: 100%;
  width: 100%;
  img {
    object-fit: cover;
    height: 100%;
    width: 100%;
    min-height: 70rem;
    border-top-right-radius: 3rem;
    border-bottom-right-radius: 3rem;
    ${breakpoints.down(`md`)} {
      min-height: unset;
      border-top-right-radius: 0rem;
      border-bottom-left-radius: 2rem;
      border-bottom-right-radius: 2rem;
    }
  }

  position: relative;

  .only-mobile {
    position: absolute;
    bottom: 2rem;
    left: 2rem;
    filter: invert(1);
    background: white;
    border-color: white;
    padding: 1rem 2rem;
  }
`;

const ReadMore = styled.a`
  padding: 1.5rem 3rem;
  background: transparent;
  border: 1px solid black;
  border-radius: 3rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  color: black;
  text-decoration: none;
  appearance: none;

  transition: background-color 0.2s linear, color 0.2s linear;

  &&:hover {
    background: black;
    color: white;
  }
`;
